document.addEventListener('DOMContentLoaded', () => {
  if (typeof gsap === 'undefined') {
    console.warn('GSAP not loaded. Global animations disabled.');
    return;
  }

  const settings = window.GSAP_THEME_SETTINGS || {};
  
  // Helper: Create Observer
  const createObserver = (selector, callback, threshold = 0.1) => {
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          callback(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: threshold });

    elements.forEach(el => observer.observe(el));
  };

  // --- Headings ---
  if (settings.headings_enable) {
    const headingSelector = 'h1:not(.site-title), h2, h3, .h0, .h1, .h2, .h3';
    
    createObserver(headingSelector, (el) => {
      // Avoid re-animating or conflicting
      if(el.dataset.gsapAnimated) return;
      el.dataset.gsapAnimated = "true";

      const style = settings.headings_style;

      switch(style) {
        case 'fade-up':
          gsap.fromTo(el, 
            { autoAlpha: 0, y: 30 }, 
            { autoAlpha: 1, y: 0, duration: 1, ease: 'power2.out' }
          );
          break;
        case 'slide-left':
          gsap.fromTo(el, 
            { autoAlpha: 0, x: -50 }, 
            { autoAlpha: 1, x: 0, duration: 1, ease: 'power2.out' }
          );
          break;
        case 'slide-right':
          gsap.fromTo(el, 
            { autoAlpha: 0, x: 50 }, 
            { autoAlpha: 1, x: 0, duration: 1, ease: 'power2.out' }
          );
          break;
        case 'chars-stagger':
          animateTextSplit(el, 'chars');
          break;
        case 'words-stagger':
          animateTextSplit(el, 'words');
          break;
        case 'typewriter':
          animateTypewriter(el);
          break;
        default:
          gsap.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1 });
      }
    });
  }

  // --- Body Text ---
  if (settings.body_enable) {
    const bodySelector = '.rte p, .image-with-text__text, .rich-text__text';
    createObserver(bodySelector, (el) => {
      if(el.dataset.gsapAnimated) return;
      el.dataset.gsapAnimated = "true";

      if (settings.body_style === 'fade-in') {
        gsap.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1, delay: 0.2 });
      } else {
        // fade-up
        gsap.fromTo(el, 
          { autoAlpha: 0, y: 20 }, 
          { autoAlpha: 1, y: 0, duration: 1, ease: 'power2.out', delay: 0.2 }
        );
      }
    });
  }

  // --- Images ---
  if (settings.images_enable) {
    const imgSelector = '.media img, .card__media img, .article-card__image';
    createObserver(imgSelector, (el) => {
      // Ensure wrapper is overflow hidden for zoom effects
      const wrapper = el.closest('.media') || el.parentElement;
      if (wrapper) wrapper.style.overflow = 'hidden';

      const style = settings.images_style;
      
      switch(style) {
        case 'zoom-in':
          gsap.fromTo(el, 
            { scale: 1.15, autoAlpha: 0 }, 
            { scale: 1, autoAlpha: 1, duration: 1.2, ease: 'power2.out' }
          );
          break;
        case 'slide-up':
          gsap.fromTo(el, 
            { y: 50, autoAlpha: 0 }, 
            { y: 0, autoAlpha: 1, duration: 1, ease: 'power2.out' }
          );
          break;
        case 'fade-in':
          gsap.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.2 });
          break;
      }
    });
  }

  // --- Buttons ---
  if (settings.buttons_enable) {
    const buttons = document.querySelectorAll('.button, .btn, .shopify-payment-button__button');
    
    // Initial Entrance
    createObserver('.button, .btn, .shopify-payment-button__button', (el) => {
      gsap.fromTo(el, 
        { autoAlpha: 0, scale: 0.9 }, 
        { autoAlpha: 1, scale: 1, duration: 0.5, ease: 'back.out(1.7)' }
      );
    });

    // Hover Effects
    buttons.forEach(btn => {
      btn.addEventListener('mouseenter', () => {
        switch(settings.buttons_hover) {
          case 'scale':
            gsap.to(btn, { scale: 1.05, duration: 0.3, ease: 'power1.out' });
            break;
          case 'lift':
            gsap.to(btn, { y: -3, boxShadow: '0 5px 15px rgba(0,0,0,0.2)', duration: 0.3 });
            break;
          case 'wobble':
            gsap.to(btn, { rotation: 3, duration: 0.1, yoyo: true, repeat: 3 });
            gsap.to(btn, { rotation: 0, duration: 0.1, delay: 0.4 });
            break;
          case 'pulse':
            gsap.to(btn, { scale: 1.05, duration: 0.5, yoyo: true, repeat: -1 });
            break;
        }
      });

      btn.addEventListener('mouseleave', () => {
        if(settings.buttons_hover === 'pulse') {
            gsap.killTweensOf(btn);
        }
        gsap.to(btn, { scale: 1, y: 0, rotation: 0, boxShadow: 'none', duration: 0.3 });
      });
    });
  }

  // --- SVG Icons ---
  if (settings.icons_enable) {
    // Select SVGs in common icon containers
    const iconSelector = '.contact-icon svg, .social-icons svg, .icon-with-text svg';
    createObserver(iconSelector, (el) => {
      if(el.dataset.gsapAnimated) return;
      el.dataset.gsapAnimated = "true";

      const style = settings.icons_style;
      const duration = settings.icons_duration || 0.5;

      switch(style) {
        case 'pulse':
          gsap.fromTo(el, 
            { scale: 0.8, autoAlpha: 0 }, 
            { scale: 1, autoAlpha: 1, duration: duration, ease: 'elastic.out(1, 0.5)' }
          );
          break;
        case 'spin':
          gsap.fromTo(el, 
            { rotation: 0, autoAlpha: 0 }, 
            { rotation: 360, autoAlpha: 1, duration: duration * 2, ease: 'power2.out' }
          );
          break;
        case 'fade-in':
          gsap.fromTo(el, 
            { autoAlpha: 0 }, 
            { autoAlpha: 1, duration: duration }
          );
          break;
        case 'slide-up':
           gsap.fromTo(el, 
            { y: 20, autoAlpha: 0 }, 
            { y: 0, autoAlpha: 1, duration: duration, ease: 'power2.out' }
          );
          break;
        default:
          gsap.fromTo(el, 
            { y: 10, autoAlpha: 0 }, 
            { y: 0, autoAlpha: 1, duration: duration, ease: 'power2.out' }
          );
      }
    });
  }

  // --- Helper Functions ---

  function animateTextSplit(el, type) {
    // IMPORTANT: Make parent visible immediately so children can be seen animating
    gsap.set(el, { autoAlpha: 1 });

    const text = el.innerText;
    // Check if it has nested tags (like links), if so, skip split to avoid breakage
    if (el.children.length > 0) {
      // Fallback to fade up
      gsap.fromTo(el, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 1 });
      return;
    }

    if (type === 'chars') {
      el.innerHTML = text.split('').map(char => `<span style="display:inline-block; opacity:0;">${char === ' ' ? '&nbsp;' : char}</span>`).join('');
      gsap.to(el.children, { opacity: 1, y: 0, duration: 0.5, stagger: 0.05, ease: 'power2.out' });
      gsap.from(el.children, { y: 20, duration: 0.5, stagger: 0.05, ease: 'power2.out' }, "<");
    } else {
      // words
      el.innerHTML = text.split(' ').map(word => `<span style="display:inline-block; opacity:0;">${word}</span>`).join(' ');
      gsap.to(el.children, { opacity: 1, duration: 0.8, stagger: 0.1, ease: 'power2.out' });
      gsap.from(el.children, { y: 20, duration: 0.8, stagger: 0.1, ease: 'power2.out' }, "<");
    }
  }

  function animateTypewriter(el) {
    // IMPORTANT: Make parent visible
    gsap.set(el, { autoAlpha: 1 });

    if (el.children.length > 0) {
      gsap.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1 });
      return;
    }
    const text = el.innerText;
    el.innerText = '';
    
    let i = 0;
    const interval = setInterval(() => {
      el.innerText += text.charAt(i);
      i++;
      if (i >= text.length) clearInterval(interval);
    }, 50); // Speed
  }
});
